import { useState } from "react"
import Dialog from "@mui/material/Dialog"
import Button from "@mui/material/Button"
import TextField from "@mui/material/TextField"
import Divider from "@mui/material/Divider"
import IconButton from "@mui/material/IconButton"
import CloseIcon from "@mui/icons-material/Close"
import GoogleIcon from "@mui/icons-material/Google"
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
} from "firebase/auth"
import { auth, googleProvider } from "./firebase"
import { useAuthContext } from "./context/AuthContext"
import { event } from "./auth"

const ERROR_MESSAGES = {
  "auth/invalid-credential": "Incorrect email or password.",
  "auth/user-not-found": "Incorrect email or password.",
  "auth/wrong-password": "Incorrect email or password.",
  "auth/invalid-email": "That email address doesn't look valid.",
  "auth/email-already-in-use": "An account already exists for that email.",
  "auth/weak-password": "Password must be at least 6 characters.",
  "auth/too-many-requests": "Too many attempts — try again in a bit.",
  "auth/network-request-failed": "Network error — check your connection.",
}

const SILENT_ERRORS = new Set([
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
])

export const LoginModal = () => {
  const authCtx = useAuthContext()
  const [mode, setMode] = useState("signin") // "signin" | "signup"
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  // email a reset link was sent to; non-null swaps in the confirmation view
  const [resetSentTo, setResetSentTo] = useState(null)

  const isSignup = mode === "signup"

  function reset() {
    setEmail("")
    setPassword("")
    setError(null)
    setBusy(false)
    setResetSentTo(null)
  }

  function close() {
    authCtx.closeLogin()
    reset()
    setMode("signin")
  }

  async function run(fn, eventName) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      event(eventName, "/auth")
      close()
    } catch (err) {
      if (!SILENT_ERRORS.has(err?.code)) {
        setError(ERROR_MESSAGES[err?.code] ?? "Something went wrong, please try again.")
      }
      setBusy(false)
    }
  }

  const submitEmail = (e) => {
    e.preventDefault()
    if (!email || !password) return
    if (isSignup) {
      run(
        () => createUserWithEmailAndPassword(auth, email, password),
        "signup",
      )
    } else {
      run(() => signInWithEmailAndPassword(auth, email, password), "login")
    }
  }

  const submitGoogle = () => {
    run(() => signInWithPopup(auth, googleProvider), "login-google")
  }

  const sendReset = async () => {
    if (!email) {
      setError("Enter your email above first.")
      return
    }
    setBusy(true)
    setError(null)
    try {
      await sendPasswordResetEmail(auth, email)
      event("password-reset", "/auth")
      setResetSentTo(email)
    } catch (err) {
      if (err?.code === "auth/user-not-found") {
        // projects without email enumeration protection reject unknown
        // addresses; show the neutral confirmation instead of leaking
        // whether an account exists
        setResetSentTo(email)
      } else {
        setError(
          ERROR_MESSAGES[err?.code] ??
            "Couldn't send the reset email, please try again.",
        )
      }
    }
    setBusy(false)
  }

  return (
    <Dialog
      open={authCtx.loginOpen}
      onClose={close}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "var(--surface-2)",
          border: "1px solid var(--border)",
          backgroundImage: "none",
        },
      }}
    >
      <form onSubmit={submitEmail} style={{ padding: "20px 24px 24px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <span
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: "var(--text)",
            }}
          >
            {resetSentTo
              ? "Check your inbox"
              : isSignup
                ? "Create account"
                : "Log in"}
          </span>
          <IconButton size="small" onClick={close} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {resetSentTo ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <span style={{ fontSize: 13, color: "var(--text)" }}>
              If an account exists for <b>{resetSentTo}</b>, a password reset
              link is on its way. Follow it to set a new password, then come
              back and log in.
            </span>
            <Button variant="outlined" onClick={() => setResetSentTo(null)}>
              Back to log in
            </Button>
          </div>
        ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <TextField
            size="small"
            type="email"
            label="Email"
            value={email}
            autoComplete="email"
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
          />
          <TextField
            size="small"
            type="password"
            label="Password"
            value={password}
            autoComplete={isSignup ? "new-password" : "current-password"}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
          />

          {!isSignup && (
            <button
              type="button"
              onClick={sendReset}
              disabled={busy}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                color: "var(--muted)",
                fontSize: 12,
                alignSelf: "flex-end",
                marginTop: -6,
              }}
            >
              Forgot password?
            </button>
          )}

          {error && (
            <span style={{ fontSize: 12, color: "var(--danger)" }}>
              {error}
            </span>
          )}

          <Button
            type="submit"
            variant="contained"
            disableElevation
            disabled={busy || !email || !password}
          >
            {isSignup ? "Create account" : "Log in"}
          </Button>

          <Divider sx={{ fontSize: 11, color: "var(--muted)" }}>or</Divider>

          <Button
            variant="outlined"
            onClick={submitGoogle}
            disabled={busy}
            startIcon={<GoogleIcon />}
          >
            Continue with Google
          </Button>

          <span
            style={{
              fontSize: 12,
              color: "var(--muted)",
              textAlign: "center",
            }}
          >
            {isSignup ? "Already have an account?" : "No account yet?"}{" "}
            <button
              type="button"
              onClick={() => {
                setMode(isSignup ? "signin" : "signup")
                setError(null)
              }}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                color: "var(--accent)",
                fontSize: 12,
              }}
            >
              {isSignup ? "Log in" : "Create one"}
            </button>
          </span>
        </div>
        )}
      </form>
    </Dialog>
  )
}
